import {TrackKey, TrackModel} from 'models/configuration/TrackModel'
import {EventModel} from 'models/EventModel'
import {RegistrationModel} from 'models/registration/RegistrationModel'
import {WysiwygType} from 'types'
import {Memoize} from 'util/memoize'

export type TourKey = string

export type TourConfig = {
	key: TourKey
	label: string
	event: TrackKey
	timeslot: TourTimeslot
	time: string
	capacity: number
	spots: number
	popup: WysiwygType
}

export type TourTimeslot = 'am' | 'pm'

export class TourModel {
	event: EventModel
	config: TourConfig

	constructor(event: EventModel, config: TourConfig) {
		this.event = event
		this.config = config
	}

	@Memoize()
	get key(): TourKey {
		return this.config.key
	}

	@Memoize()
	get isFull(): boolean {
		return this.config.spots <= 0
	}

	@Memoize()
	get title(): TourKey {
		return this.config.label
	}

	@Memoize()
	get popup(): WysiwygType {
		return this.config.popup
	}

	@Memoize()
	get time(): string {
		return this.config.time
	}

	@Memoize()
	get track(): TrackModel {
		return this.event.tracks.find(event => event.key === this.config.event)
	}

	@Memoize()
	get label(): string {
		return this.config.label
	}

	@Memoize()
	get date(): string {
		if (!this.track) return ''
		return this.track.date
	}

	@Memoize()
	get timeslot(): string {
		return this.config.timeslot
	}

	@Memoize()
	get capacity(): number {
		return this.config.capacity
	}

	@Memoize()
	get conflictingTours(): Array<TourModel> {
		return this.event.tours.filter(
			tour =>
				tour !== this &&
				tour.timeslot === this.timeslot &&
				tour.date === this.date
		)
	}

	laterSelectedToursFor(registration: RegistrationModel): Array<TourModel> {
		const index = registration.tours.indexOf(this)
		return registration.tours.slice(index)
	}

	hasConflictingToursFor(registration: RegistrationModel): boolean {
		const conflictingToursSet = new Set(this.conflictingTours)
		return !!this.laterSelectedToursFor(registration).find(tour =>
			conflictingToursSet.has(tour)
		)
	}

	isApplicableFor(registration: RegistrationModel): boolean {
		return registration.tracksSet.has(this.track)
	}
}
